import {
  createInternalMessageWithTimedVisibility,
  ResponseWithVisibility,
} from "features/CenterContent/RoleContent/InternalMessage/utils/queries";
import {
  Version,
  VersionEvent,
} from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/Version";
import { useActionButtons } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/useActionButtons";
import { ProductionDeviationButton } from "features/CenterContent/VehicleDetails/shared/ProductionDeviationButton";
import { Dispatch, FC, ReactNode, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";
import { DropsStaticAlert, HStack, Text } from "shared/components/Spor";
import { Semantics } from "shared/components/drops/DropsBadge";
import { Title } from "shared/components/feedback/InfoMessage/ExpandableInfoMessage/ExpandableButton";
import { ExpandableInfoMessage } from "shared/components/feedback/InfoMessage/ExpandableInfoMessage/ExpandableInfoMessage";
import { useThemeSemantic } from "shared/theme/themeSemantic";
import { useDropsRole } from "stores/useDropsRole";
import styled from "styled-components";

const Versions = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 12px;
  list-style: none;
  padding: 0;
  margin: 0px 30px 0px 0;
`;

type EventInfoMessageProps = {
  title: string;
  subTitle: string;
  semantics: Semantics;
  isPlannedMaintenance?: boolean;
  versions: VersionEvent[];
  showActionButtons: boolean;
  askConfirmation: boolean;
  setAskConfirmation: Dispatch<SetStateAction<boolean>>;
  setModalOpen: Dispatch<SetStateAction<boolean>>;
  onClose: () => Promise<ResponseWithVisibility | void>;
  dropsLogTextInput?: ReactNode;
  initialOpenState?: boolean;
  isDepublish?: boolean;
  infrastructureEventUuid?: string;
  productionDeviationUuid?: string;
};

export const EventInfoMessage: FC<EventInfoMessageProps> = ({
  title,
  subTitle,
  semantics,
  isPlannedMaintenance = false,
  versions,
  showActionButtons,
  askConfirmation,
  setAskConfirmation,
  setModalOpen,
  onClose,
  dropsLogTextInput,
  initialOpenState = false,
  isDepublish = false,
  infrastructureEventUuid,
  productionDeviationUuid,
}) => {
  const theme = useThemeSemantic(semantics);
  const { t } = useTranslation();
  const [isError, setIsError] = useState<boolean>(false);

  const isClosed = semantics === "success";
  const isProductionDeviation = !!productionDeviationUuid && !isClosed; // No longer affected by productionDeviation if it is closed

  const { isDriftledningen } = useDropsRole();

  const actionButtons = useActionButtons(
    versions,
    setModalOpen,
    isPlannedMaintenance,
    !!infrastructureEventUuid,
    () => {
      setIsError(false);
      const latestInternalMessage = versions[0].internalMessages;
      onClose()
        .then((response) =>
          createInternalMessageWithTimedVisibility(
            latestInternalMessage,
            response,
          ),
        ) // Will only be performed if latestInternalMessage is not undefined/null
        .catch(() => setIsError(true));
    },

    askConfirmation,
    setAskConfirmation,
    isError,
    isDepublish,
    infrastructureEventUuid || "",
  );
  if (versions.length === 0) return null;
  const confirmText = `${t("Er du sikker på at du vil")} ${isDepublish ? t("depublisere") : t("friskmelde")} ${t("hendelsen?")}`;
  return (
    <ExpandableInfoMessage
      style={{ width: "100%" }}
      semantics={semantics}
      title={
        <HStack alignItems="center" justifyContent="space-between" w="100%">
          <Title>{title}</Title>
          {isProductionDeviation && <ProductionDeviationButton />}
        </HStack>
      }
      subTitle={subTitle}
      actionButtons={showActionButtons ? actionButtons : []}
      initialOpenState={initialOpenState}
      isProductionDeviation={isProductionDeviation}
    >
      <Versions>
        {versions.map((version, index) => (
          <Version
            key={(version.message ?? "") + version.updatedAt}
            {...version}
            semantics={semantics}
            isLastElement={index === versions.length - 1}
          />
        ))}
        {askConfirmation && (
          <Text
            variant="sm"
            fontWeight="bold"
            borderTop={`1px dashed ${theme.colorSemanticOutline}`}
            pt="18px"
          >
            {confirmText}
          </Text>
        )}
        {askConfirmation && !isDriftledningen() && dropsLogTextInput}
        {isError && (
          <DropsStaticAlert variant="error" mb="24px">
            {t("Noe gikk galt, vennligst prøv igjen")}
          </DropsStaticAlert>
        )}
      </Versions>
    </ExpandableInfoMessage>
  );
};
